import * as XLSX from 'xlsx';
import * as XlsxPopulate from 'xlsx-populate/browser/xlsx-populate';

import { AddressData } from 'api';
import { CreateOrderInterface } from 'slice';
import {
  convertWorkBookToBlob,
  genWholesaleUnit,
  getDummyArray,
  getFullAddress,
  ORDER_DELIVERY_METHOD,
  ORDER_PAYMENT_METHOD,
  removeSpaceString,
} from 'utils';

export interface UpdateCreateOrderInfo {
  selectedProducts?: any[];
  addressID?: string;
  paymentMethod?: ORDER_PAYMENT_METHOD;
  deliveryMethod?: ORDER_DELIVERY_METHOD;
  currentAddress?: AddressData | undefined;
  deliveryFee?: number | undefined;
  deliveryFeeType?: number;
  entityShippingPolicyItemID?: string | string;
  promotions?: any[];
  oldEntityPricePolicyID?: string;
  entityPricePolicyID?: string;
  entityProposalPolicyID?: string;
  oldEntityProposalPolicyID?: string;
  shippingPolicyItems?: any[];
  distanceShippingFees?: any[];
  customShippingFees?: any[];
  selectedOrderSalePromotionCondition?: any;
}

export interface UpdateCreateOrderBody {
  supplierID?: string;
  customerID: string;
  oldCustomerID?: string;
  customerNote: string;
  addressID: string | undefined;
  paymentMethod: ORDER_PAYMENT_METHOD;
  deliveryMethod: ORDER_DELIVERY_METHOD;
  selectedProducts: any[];
  promotions?: any[];
  oldEntityPricePolicyID?: string;
  entityPricePolicyID?: string;
  entityProposalPolicyID?: string;
  oldEntityProposalPolicyID?: string;
}

export const getBodyCalcProductItemForCreateOrder = (
  currentOrder: CreateOrderInterface,
  {
    selectedProducts,
    addressID,
    paymentMethod,
    deliveryMethod,
    currentAddress,
    deliveryFee,
    deliveryFeeType,
    entityShippingPolicyItemID,
    promotions,
    entityPricePolicyID,
    entityProposalPolicyID,
    shippingPolicyItems,
    distanceShippingFees,
    customShippingFees,
    selectedOrderSalePromotionCondition,
  }: UpdateCreateOrderInfo
): UpdateCreateOrderBody => {
  const selectedProductItemFromStore = currentOrder.selectedProducts;

  const formatSelectedProductItems = (
    selectedProducts || selectedProductItemFromStore
  ).map((productItem: any) => {
    const {
      id,
      productID,
      orderItemID,
      proposalItemID,
      customerNote,
      supplierNote,
      currentPrice,
      originPrice,
      customerQuantity,
      reportType,
      absoluteQuantity,
      relativeQuantity,
      oldPriceUnit,
      selectedPriceUnit,
      productPricePolicyID,
      productProposalPolicyID,
      isPriceChanged,
      promotions: itemPromotions,
      salePromotionConditions,
    } = productItem;

    return {
      productID: productID ?? id,
      orderItemID,
      proposalItemID,
      currentPrice,
      originPrice,
      customerQuantity,
      customerNote,
      supplierNote,
      productPricePolicyID,
      productProposalPolicyID,
      action: 'update',
      priceUnit: selectedPriceUnit,
      oldPriceUnit,
      salePromotionConditions,
      isPriceChanged,
      reportQuantity: {
        reportType,
        absoluteQuantity,
        relativeQuantity,
      },
      promotions: itemPromotions,
    };
  });

  const body = {
    currentAddress,
    addressID: currentAddress?.id,
    customerID: currentOrder.customerInfo?.id,
    chainCustomerID: currentOrder.customerInfo?.chainCustomerID,
    customerNote: currentOrder.customerNote,
    supplierNote: currentOrder.supplierNote,
    entityPricePolicyID:
      entityPricePolicyID ?? currentOrder?.entityPricePolicyID,
    entityProposalPolicyID: currentOrder?.entityProposalPolicyID,
    paymentMethod:
      typeof paymentMethod == 'number'
        ? paymentMethod
        : currentOrder.paymentMethod,
    deliveryMethod:
      typeof deliveryMethod == 'number'
        ? deliveryMethod
        : currentOrder.deliveryMethod,
    selectedProducts: formatSelectedProductItems,
    // deliveryFee,
    // entityShippingPolicyItemID,
    deliveryFeeType: deliveryFeeType ?? currentOrder?.deliveryFeeType,
    orderEntityShippingPolicyItem: (
      shippingPolicyItems || currentOrder?.shippingPolicyItems
    )?.find(item => item.isSelected),
    orderEntityDistanceShippingFee: (
      distanceShippingFees || currentOrder?.distanceShippingFees
    )?.find(item => item.isSelected),
    orderEntityCustomShippingFees:
      (customShippingFees || currentOrder?.customShippingFees)?.filter(
        item => item.isSelected
      ) ?? [],
    promotions: promotions ?? currentOrder?.promotions ?? [],
    selectedOrderSalePromotionCondition:
      selectedOrderSalePromotionCondition ??
      currentOrder?.selectedOrderSalePromotionCondition,
  };

  return body;
};

export const getBodyCalcProductItemForUpdateOrder = (
  currentOrder: any,
  {
    selectedProducts,
    addressID,
    paymentMethod,
    deliveryMethod,
    currentAddress,
    deliveryFee,
    deliveryFeeType,
    entityShippingPolicyItemID,
    promotions,
    entityPricePolicyID,
    shippingPolicyItems,
    distanceShippingFees,
    customShippingFees,
    selectedOrderSalePromotionCondition,
  }: any
): any => {
  const selectedProductItemFromStore = currentOrder.selectedProducts;

  const formatSelectedProductItems = (
    selectedProducts || selectedProductItemFromStore
  ).map((productItem: any) => {
    const {
      id,
      productID,
      orderItemID,
      proposalItemID,
      customerNote,
      supplierNote,
      currentPrice,
      originPrice,
      customerQuantity,
      reportQuantity,
      priceUnit,
      productPricePolicyID,
      productProposalPolicyID,
      promotions: itemPromotions,
      salePromotionConditions,
    } = productItem;

    return {
      productID: productID ?? id,
      orderItemID,
      proposalItemID,
      currentPrice,
      originPrice,
      customerQuantity,
      customerNote,
      supplierNote,
      productPricePolicyID,
      productProposalPolicyID,
      action: 'update',
      priceUnit,
      reportQuantity,
      promotions: itemPromotions,
      salePromotionConditions,
    };
  });

  const body = {
    currentAddress,
    orderID: currentOrder?.orderID,
    customerID: currentOrder?.customerID,
    customerNote: currentOrder?.customerNote,
    supplierNote: currentOrder?.supplierNote,
    addressID: addressID || currentOrder?.address?.id,
    entityPricePolicyID:
      entityPricePolicyID ?? currentOrder?.entityPricePolicyID,
    entityProposalPolicyID: currentOrder?.entityProposalPolicyID,
    paymentMethod:
      typeof paymentMethod == 'number'
        ? paymentMethod
        : currentOrder.paymentMethod,
    deliveryMethod:
      typeof deliveryMethod == 'number'
        ? deliveryMethod
        : currentOrder.deliveryMethod,
    selectedProducts: formatSelectedProductItems,
    // deliveryFee,
    // entityShippingPolicyItemID,
    deliveryFeeType: deliveryFeeType ?? currentOrder?.deliveryFeeType,
    orderEntityShippingPolicyItem: (
      shippingPolicyItems || currentOrder?.shippingPolicyItems
    )?.find(item => item.isSelected),
    orderEntityDistanceShippingFee: (
      distanceShippingFees || currentOrder?.distanceShippingFees
    )?.find(item => item.isSelected),
    orderEntityCustomShippingFees:
      (customShippingFees || currentOrder?.customShippingFees)?.filter(
        item => item.isSelected
      ) ?? [],
    promotions: promotions ?? currentOrder?.promotions ?? [],
    selectedOrderSalePromotionCondition:
      selectedOrderSalePromotionCondition ||
      currentOrder?.selectedOrderSalePromotionCondition ||
      {},
  };

  return body;
};

export const getBodyCalcProductItemForCreateBill = (
  currentOrder: any,
  {
    selectedProducts,
    addressID,
    paymentMethod,
    deliveryMethod,
    currentAddress,
    deliveryFee,
    deliveryFeeType,
    entityShippingPolicyItemID,
    promotions,
    depositMoney,
    paidMoney,
    deductedMoney,
    excessMoney,
    isDebt,
    debtMoney,
    oldEntityPricePolicyID,
    entityPricePolicyID,
    shippingPolicyItems,
    distanceShippingFees,
    customShippingFees,
    selectedOrderSalePromotionCondition,
  }: any
): any => {
  const selectedProposalItemFromStore = currentOrder.selectedProducts;

  const formatSelectedProductItems = (
    selectedProducts || selectedProposalItemFromStore
  ).map((productItem: any) => {
    const {
      id,
      subID,
      customerNote,
      supplierNote,
      currentPrice,
      originPrice,
      customerQuantity,
      reportType,
      absoluteQuantity,
      relativeQuantity,
      selectedPriceUnit,
      oldPriceUnit,
      proposalItemID,
      productPricePolicyID,
      isPriceChanged,
      promotions: itemPromotions,
      salePromotionConditions,
    } = productItem;

    return {
      customerNote,
      supplierNote,
      proposalItemID,
      productPricePolicyID,
      productID: id,
      subID,
      currentPrice,
      originPrice,
      customerQuantity,
      action: 'update',
      oldPriceUnit,
      priceUnit: selectedPriceUnit,
      isPriceChanged,
      salePromotionConditions,
      reportQuantity: {
        reportType,
        absoluteQuantity,
        relativeQuantity,
      },
      promotions: itemPromotions,
    };
  });

  const body = {
    orderID: currentOrder.orderID,
    currentAddress,
    addressID: currentAddress?.id,
    customerNote: currentOrder.customerNote,
    supplierNote: currentOrder.supplierNote,
    customerID: currentOrder?.customerInfo?.id,
    chainCustomerID: currentOrder?.customerInfo?.chainCustomerID,
    entityPricePolicyID:
      entityPricePolicyID ?? currentOrder?.entityPricePolicyID,
    entityProposalPolicyID: currentOrder?.entityProposalPolicyID,
    paymentMethod: paymentMethod ?? currentOrder.paymentMethod,
    deliveryMethod: deliveryMethod ?? currentOrder.deliveryMethod,
    selectedProducts: formatSelectedProductItems,
    // deliveryFee,
    deliveryFeeType: deliveryFeeType ?? currentOrder?.deliveryFeeType,
    // entityShippingPolicyItemID,
    billEntityShippingPolicyItem: (
      shippingPolicyItems || currentOrder?.shippingPolicyItems
    )?.find(item => item.isSelected),
    billEntityDistanceShippingFee: (
      distanceShippingFees || currentOrder?.distanceShippingFees
    )?.find(item => item.isSelected),
    billEntityCustomShippingFees:
      (customShippingFees || currentOrder?.customShippingFees)?.filter(
        item => item.isSelected
      ) ?? [],
    promotions: promotions ?? currentOrder?.promotions ?? [],
    selectedOrderSalePromotionCondition:
      selectedOrderSalePromotionCondition ??
      currentOrder?.selectedOrderSalePromotionCondition ??
      {},
    adjustments: currentOrder.summaryBill?.adjustments ?? [],
    totalPrice: Number(currentOrder?.summaryBill?.totalPrice ?? 0) ?? 0,
    returnMoney: Number(currentOrder?.returnMoney ?? 0),
    depositMoney: depositMoney ?? currentOrder?.depositMoney,
    paidMoney: paidMoney ?? currentOrder?.paidMoney,
    deductedMoney: Math.abs(deductedMoney ?? currentOrder?.deductedMoney ?? 0),
    excessMoney: Math.abs(excessMoney ?? currentOrder?.excessMoney ?? 0),
    isDebt: isDebt ?? currentOrder?.isDebt,
    debtMoney: Math.abs(debtMoney ?? currentOrder?.debtMoney ?? 0),
  };

  return body;
};

export const getBodyCalcProductItemForUpdateBill = (
  currentOrder: any,
  {
    selectedProducts,
    addressID,
    paymentMethod,
    deliveryMethod,
    currentAddress,
    deliveryFee,
    deliveryFeeType,
    entityShippingPolicyItemID,
    promotions,
    depositMoney,
    paidMoney,
    deductedMoney,
    excessMoney,
    isDebt,
    debtMoney,
    oldEntityPricePolicyID,
    entityPricePolicyID,
    shippingPolicyItems,
    distanceShippingFees,
    customShippingFees,
    selectedOrderSalePromotionCondition,
  }: any
): any => {
  const selectedProposalItemFromStore = currentOrder.selectedProducts;

  const formatSelectedProductItems = (
    selectedProducts || selectedProposalItemFromStore
  ).map((productItem: any) => {
    const {
      id,
      subID,
      billItemID,
      productID,
      customerNote,
      supplierNote,
      currentPrice,
      originPrice,
      customerQuantity,
      reportType,
      absoluteQuantity,
      relativeQuantity,
      priceUnit,
      oldPriceUnit,
      proposalItemID,
      productPricePolicyID,
      promotions: itemPromotions,
      salePromotionConditions,
    } = productItem;

    return {
      billItemID,
      customerNote,
      supplierNote,
      proposalItemID,
      productPricePolicyID,
      productID,
      subID,
      currentPrice,
      originPrice,
      customerQuantity,
      action: 'update',
      oldPriceUnit,
      priceUnit,
      reportQuantity: {
        reportType,
        absoluteQuantity,
        relativeQuantity,
      },
      promotions: itemPromotions,
      salePromotionConditions,
    };
  });

  const body = {
    currentAddress,
    billID: currentOrder.billID,
    customerNote: currentOrder.customerNote,
    supplierNote: currentOrder.supplierNote,
    customerID: currentOrder?.customer?.id,
    addressID: addressID || currentOrder.address?.id,
    entityPricePolicyID:
      entityPricePolicyID ?? currentOrder?.entityPricePolicyID,
    oldEntityPricePolicyID:
      oldEntityPricePolicyID ?? currentOrder?.oldEntityPricePolicyID,
    entityProposalPolicyID: currentOrder?.entityProposalPolicyID,
    paymentMethod:
      typeof paymentMethod == 'number'
        ? paymentMethod
        : currentOrder.paymentMethod,
    deliveryMethod:
      typeof deliveryMethod == 'number'
        ? deliveryMethod
        : currentOrder.deliveryMethod,
    selectedProducts: formatSelectedProductItems,
    // deliveryFee,
    deliveryFeeType: deliveryFeeType ?? currentOrder?.deliveryFeeType,
    // entityShippingPolicyItemID,
    billEntityShippingPolicyItem: (
      shippingPolicyItems || currentOrder?.shippingPolicyItems
    )?.find(item => item.isSelected),
    billEntityDistanceShippingFee: (
      distanceShippingFees || currentOrder?.distanceShippingFees
    )?.find(item => item.isSelected),
    billEntityCustomShippingFees:
      (customShippingFees || currentOrder?.customShippingFees)?.filter(
        item => item.isSelected
      ) ?? [],
    promotions: promotions ?? currentOrder?.promotions ?? [],
    selectedOrderSalePromotionCondition:
      selectedOrderSalePromotionCondition ??
      currentOrder?.selectedOrderSalePromotionCondition ??
      {},
    totalPrice: Number(currentOrder?.summaryBill?.totalPrice ?? 0) ?? 0,
    returnMoney: Number(currentOrder?.returnMoney ?? 0),
    depositMoney: depositMoney ?? currentOrder?.depositMoney,
    paidMoney: paidMoney ?? currentOrder?.paidMoney,
    deductedMoney: Math.abs(deductedMoney ?? currentOrder?.deductedMoney ?? 0),
    excessMoney: Math.abs(excessMoney ?? currentOrder?.excessMoney ?? 0),
    isDebt: isDebt ?? currentOrder?.isDebt,
    debtMoney: Math.abs(debtMoney ?? currentOrder?.debtMoney ?? 0),
  };

  return body;
};

export const getBodyCalcProductItemForReturnBill = (
  currentOrder: any,
  {
    selectedProducts,
    addressID,
    paymentMethod,
    deliveryMethod,
    currentAddress,
    deliveryFee,
    deliveryFeeType,
    entityShippingPolicyItemID,
    promotions,
    depositMoney,
    paidMoney,
    deductedMoney,
    excessMoney,
    returnFee,
    isDebt,
    debtMoney,
    oldEntityPricePolicyID,
    entityPricePolicyID,
  }: any
): any => {
  const selectedProposalItemFromStore = currentOrder.selectedProducts;

  const formatSelectedProductItems = (
    selectedProducts || selectedProposalItemFromStore
  ).map((productItem: any) =>
    productItem.action == 'return'
      ? {
          billItemID: productItem.billItemID,
          returnQuantity: productItem.customerQuantity,
        }
      : {
          productPricePolicyID: productItem.productPricePolicyID,
          proposalItemID: productItem?.proposalItemID,
          productID: productItem?.productID,
          billItemID: productItem?.billItemID,
          customerQuantity: productItem.customerQuantity,
          currentPrice: productItem.currentPrice,
          action: 'update',
          priceUnit: productItem?.priceUnit,
          reportQuantity: productItem.reportQuantity,
          ...(productItem?.oldPriceUnit
            ? { oldPriceUnit: productItem?.oldPriceUnit }
            : {}),
        }
  );

  const body = {
    currentAddress,
    billID: currentOrder.billID,
    customerNote: currentOrder.customerNote,
    supplierNote: currentOrder.supplierNote,
    customerID: currentOrder?.customer?.id,
    addressID: addressID || currentOrder.address?.id,
    entityPricePolicyID:
      entityPricePolicyID ?? currentOrder?.entityPricePolicyID,
    oldEntityPricePolicyID:
      oldEntityPricePolicyID ?? currentOrder?.oldEntityPricePolicyID,
    entityProposalPolicyID: currentOrder?.entityProposalPolicyID,
    paymentMethod:
      typeof paymentMethod == 'number'
        ? paymentMethod
        : currentOrder.paymentMethod,
    deliveryMethod:
      typeof deliveryMethod == 'number'
        ? deliveryMethod
        : currentOrder.deliveryMethod,
    selectedProducts: formatSelectedProductItems,
    deliveryFee,
    deliveryFeeType,
    entityShippingPolicyItemID,
    promotions: promotions ?? currentOrder?.promotions ?? [],
    totalPrice: Number(currentOrder?.summaryBill?.totalPrice ?? 0) ?? 0,
    returnMoney: Number(currentOrder?.returnMoney ?? 0),
    returnFee: returnFee ?? currentOrder?.returnFee,
    depositMoney: depositMoney ?? currentOrder?.depositMoney,
    paidMoney: paidMoney ?? currentOrder?.paidMoney,
    deductedMoney: Math.abs(deductedMoney ?? currentOrder?.deductedMoney ?? 0),
    excessMoney: Math.abs(excessMoney ?? currentOrder?.excessMoney ?? 0),
    isDebt: isDebt ?? currentOrder?.isDebt,
    debtMoney: Math.abs(debtMoney ?? currentOrder?.debtMoney ?? 0),
  };

  return body;
};

export const formatDataModalMultiConfirm = (
  data: any,
  productUnit: any,
  index?: number
): any => {
  const productList = data.proposalItems.map(proposalItem => {
    const { priceUnit } = proposalItem;
    const { id, avatar, name } = proposalItem.supplierProduct;

    const priceUnitStandard = priceUnit.find((item: any) => item.isStandard);

    return {
      id,
      name,
      avatar,
      unit: genWholesaleUnit({
        unitPrefix: productUnit.prefixObj[priceUnitStandard?.prefix ?? ''],
        unitWeight: priceUnitStandard?.weight ?? 0,
        unitPostfix: productUnit.postfixObj[priceUnitStandard?.postfix ?? 0],
      }),
      quantity: proposalItem.customerQuantity,
      originCustomerQuantity: 0,
      price: priceUnitStandard.price ?? 0,
      productOptions: proposalItem?.supplierProduct?.productOptions || [],
      originPrice: priceUnitStandard.price ?? 0,
      customerNote: proposalItem.customerNote,
      supplierNote: proposalItem.supplierNote,
    };
  });

  const summaryOrder = {
    supplierID: undefined,
    deliveryFee: data.summaryOrder.deliveryFee,
    deliveryFeeDetail: undefined,
    totalPrice: BigInt(data.summaryOrder.totalPrice),
    totalTemporaryPrice: BigInt(data.summaryOrder.totalTemporaryPrice),
    totalItem: data.summaryOrder.totalItem,
    totalAdjustment: BigInt(data.summaryOrder.totalAdjustment),
    adjustments: [],
    orders: data.summaryOrder.orders,
  };

  return {
    index,
    summaryOrder,
    productList,
    selected: true,
    customerNote: data.customerNote,
    pathID: data.autoOrderSettingDetail?.id,
    name: data.autoOrderSettingDetail?.name,
    paymentMethod: data.paymentMethod,
    deliveryMethod: data.deliveryMethod,
    address: getFullAddress(
      data.additionalInfo?.address,
      data.additionalInfo?.district?.fullName,
      data.additionalInfo?.province?.fullName,
      data.additionalInfo?.ward?.fullName
    ),
    totalPrice: summaryOrder.totalPrice,
    totalItem: summaryOrder.totalItem,
    note: undefined,
    createdAt: undefined,
  };
};

export const formatDataHistoryBillOrder = (
  data: any,
  productUnit: any
): any => {
  const productList = data.proposalItems.map(proposalItem => {
    const { priceUnit, customerProduct } = proposalItem;
    const { id, avatar, name, code } = proposalItem.supplierProduct;

    const priceUnitStandard = priceUnit.find((item: any) => item.isStandard);

    return {
      id,
      name,
      avatar,
      code: code ?? '',
      unit: genWholesaleUnit({
        unitPrefix: productUnit.prefixObj[priceUnitStandard?.prefix ?? ''],
        unitWeight: priceUnitStandard?.weight ?? 0,
        unitPostfix: productUnit.postfixObj[priceUnitStandard?.postfix ?? 0],
      }),
      quantity: proposalItem.customerQuantity,
      originCustomerQuantity: 0,
      price: priceUnitStandard?.price ?? 0,
      productOptions: proposalItem?.supplierProduct?.productOptions || [],
      originPrice: priceUnitStandard?.price ?? 0,
      customerNote: proposalItem.customerNote,
      supplierNote: proposalItem.supplierNote,
    };
  });

  return {
    supplier: {
      ...data.supplier,
    },
    productItems: productList,
    customer: {
      ...data.customer,
    },
    customerNote: data.customerNote,
    additionalInfo: {
      address: data.additionalInfo,
    },
  };
};

export const handleExportMisaOrder = (orderItems: any[]): any => {
  const ColumnCheck = [
    'MS_500',
    'Barcode',
    'InventoryItemName',
    'UnitName',
    '',
    '',
  ];

  const TitleOfColumnKey = [
    'SKUCode',
    'Barcode',
    'InventoryItemName',
    'UnitName',
    'Quantity',
    'Serials',
    'UnitPrice',
    'Amount',
    'DiscountRate',
    'DiscountAmount',
  ];

  const TitleOfColumn = [
    'Mã SKU (*)',
    'Mã vạch (*)',
    'Tên hàng hóa',
    'Đơn vị tính',
    'Số lượng (*)',
    'Serial, IMEI',
    'Đơn giá',
    'Thành tiền',
    '% KM',
    'Tiền KM',
  ];

  const centerColumns = ['B', 'D', 'E', 'F', 'G', 'H', 'I', 'J'];
  const hiddenRows = [1, 2, 3, 4, 6];
  const hiddenColumns = ['F'];

  const defaultTitleOfColumnLength = TitleOfColumn.length;
  const startGenTitleOfColumn = 6;

  const addStyles = (workbookBlob): string =>
    XlsxPopulate.fromDataAsync(workbookBlob).then(workbook => {
      const ColumnsIndex = defaultTitleOfColumnLength;
      workbook.sheets().forEach(sheet => {
        sheet.usedRange().style({
          fontFamily: 'Arial',
          border: 'thin',
        });
        sheet.row(5).height(50);
        sheet.row(7).height(20);
        sheet.cell('A5').style({
          bold: 'true',
          border: 'none',
          fontSize: '13',
          horizontalAlignment: 'center',
          verticalAlignment: 'center',
        });

        centerColumns.forEach(col => {
          sheet.column(col).style({
            horizontalAlignment: 'center',
            verticalAlignment: 'center',
          });
        });

        hiddenRows.forEach(rowNumber => {
          sheet.row(rowNumber).hidden(true);
        });

        hiddenColumns.forEach(columnName => {
          sheet.column(columnName).hidden(true);
        });

        getDummyArray(startGenTitleOfColumn).forEach(element => {
          getDummyArray(ColumnsIndex).forEach(item => {
            sheet
              .row(element.key + 1)
              .cell(item.key + 1)
              .style({
                border: 'none',
              });
          });
        });
        getDummyArray(ColumnsIndex).forEach(item => {
          sheet
            .row(7)
            .cell(item.key + 1)
            .style({
              fill: '93C47D',
              bold: 'true',
              fontSize: '9',
              horizontalAlignment: 'center',
              verticalAlignment: 'center',
            });
        });
      });
      return workbook
        .outputAsync()
        .then(newWorkBookBlob => URL.createObjectURL(newWorkBookBlob));
    });

  // create new work book
  const workbook = XLSX.utils.book_new();

  // create empty worksheet
  const worksheet = XLSX.utils.json_to_sheet([]);

  // add worksheet into work book
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Trang tính 1');

  worksheet['!merges'] = [{ s: { c: 0, r: 4 }, e: { c: 8, r: 4 } }];

  XLSX.utils.sheet_add_aoa(worksheet, [[...ColumnCheck]], {
    origin: 'A1',
    cellStyles: true,
  });

  XLSX.utils.sheet_add_aoa(worksheet, [['NHẬP KHẨU HÀNG BÁN']], {
    origin: 'A5',
    cellStyles: true,
  });

  XLSX.utils.sheet_add_aoa(worksheet, [[...TitleOfColumnKey]], {
    origin: 'A6',
    cellStyles: true,
  });

  XLSX.utils.sheet_add_aoa(worksheet, [[...TitleOfColumn]], {
    origin: 'A7',
    cellStyles: true,
  });

  XLSX.utils.sheet_add_json(worksheet, orderItems, {
    origin: 'A8',
    skipHeader: true,
  });

  const colWidths = [
    { wch: 20 },
    { wch: 15 },
    { wch: 25 },
    { wch: 15 },
    { wch: 15 },
    { wch: 15 },
    { wch: 20 },
    { wch: 15 },
    { wch: 15 },
  ];

  // add cols width
  worksheet['!cols'] = colWidths;

  const workBookBlob = convertWorkBookToBlob(workbook);

  return addStyles(workBookBlob);
};

export const handleExportMisaSupplierOrder = (orderItems: any[]): any => {
  const TitleOfColumn = [
    'Mã SKU (*)', // A
    'Mã vạch (*)', // B
    'Tên hàng hóa', // C
    'Đơn vị tính', // D
    'Kho', // E
    'Vị trí', // F
    'Số lượng (*)', // G
    'Số lô', // H
    'Hạn sử dụng', // I
    'Serial/IMEI', // J
    'Đơn giá', // K
    'Thành tiền', // L
    '% CK', // M
    'Tiền CK', // N
    'Thuế suất', // O
    'Tiền thuế', // P
    'Tiền thanh toán', // Q
  ];

  const centerColumns = [
    'B',
    'D',
    'E',
    'F',
    'G',
    'H',
    'I',
    'J',
    'K',
    'L',
    'M',
    'N',
    'O',
    'P',
    'Q',
  ];
  const hiddenRows = [];
  const hiddenColumns = ['H', 'I', 'J', 'O', 'P', 'Q'];

  const defaultTitleOfColumnLength = TitleOfColumn.length;

  const addStyles = (workbookBlob): string =>
    XlsxPopulate.fromDataAsync(workbookBlob).then(workbook => {
      const ColumnsIndex = defaultTitleOfColumnLength;
      workbook.sheets().forEach(sheet => {
        sheet.usedRange().style({
          fontFamily: 'Arial',
          border: 'thin',
        });
        sheet.row(1).height(50);
        sheet.cell('A1').style({
          bold: 'true',
          border: 'none',
          fontSize: '13',
          horizontalAlignment: 'center',
          verticalAlignment: 'center',
        });

        centerColumns.forEach(col => {
          sheet.column(col).style({
            horizontalAlignment: 'center',
            verticalAlignment: 'center',
          });
        });

        hiddenRows.forEach(rowNumber => {
          sheet.row(rowNumber).hidden(true);
        });

        hiddenColumns.forEach(columnName => {
          sheet.column(columnName).hidden(true);
        });

        getDummyArray(ColumnsIndex).forEach(item => {
          sheet
            .row(2)
            .cell(item.key + 1)
            .style({
              fill: '93C47D',
              bold: 'true',
              fontSize: '9',
              horizontalAlignment: 'center',
              verticalAlignment: 'center',
            });
        });
      });
      return workbook
        .outputAsync()
        .then(newWorkBookBlob => URL.createObjectURL(newWorkBookBlob));
    });

  // create new work book
  const workbook = XLSX.utils.book_new();

  // create empty worksheet
  const worksheet = XLSX.utils.json_to_sheet([]);

  // add worksheet into work book
  XLSX.utils.book_append_sheet(workbook, worksheet, 'NhapkhauHangmua');

  worksheet['!merges'] = [
    { s: { c: 0, r: 0 }, e: { c: TitleOfColumn.length - 1, r: 0 } },
  ];

  XLSX.utils.sheet_add_aoa(worksheet, [['NHẬP KHẨU HÀNG MUA']], {
    origin: 'A1',
    cellStyles: true,
  });

  XLSX.utils.sheet_add_aoa(worksheet, [[...TitleOfColumn]], {
    origin: 'A2',
    cellStyles: true,
  });

  XLSX.utils.sheet_add_json(worksheet, orderItems, {
    origin: 'A3',
    skipHeader: true,
  });

  const colWidths = [
    { wch: 20 },
    { wch: 15 },
    { wch: 25 },
    { wch: 15 },
    { wch: 15 },
    { wch: 15 },
    { wch: 20 },
    { wch: 15 },
    { wch: 15 },
  ];

  // add cols width
  worksheet['!cols'] = colWidths;

  const workBookBlob = convertWorkBookToBlob(workbook);

  return addStyles(workBookBlob);
};

export const handleExportMisaBill = (billItems: any[]): any => {
  const ColumnCheck = [
    'MS_500',
    'Barcode',
    'InventoryItemName',
    'UnitName',
    '',
    '',
  ];

  const TitleOfColumnKey = [
    'SKUCode',
    'Barcode',
    'InventoryItemName',
    'UnitName',
    'Quantity',
    'Serials',
    'UnitPrice',
    'Amount',
    'DiscountRate',
    'DiscountAmount',
  ];

  const TitleOfColumn = [
    'Mã SKU (*)',
    'Mã vạch (*)',
    'Tên hàng hóa',
    'Đơn vị tính',
    'Số lượng (*)',
    'Serial, IMEI',
    'Đơn giá',
    'Thành tiền',
    '% KM',
    'Tiền KM',
  ];

  const centerColumns = ['B', 'D', 'E', 'F', 'G', 'H', 'I', 'J'];
  const hiddenRows = [1, 2, 3, 4, 6];
  const hiddenColumns = ['F'];

  const defaultTitleOfColumnLength = TitleOfColumn.length;
  const startGenTitleOfColumn = 6;

  const addStyles = (workbookBlob): string =>
    XlsxPopulate.fromDataAsync(workbookBlob).then(workbook => {
      const ColumnsIndex = defaultTitleOfColumnLength;
      workbook.sheets().forEach(sheet => {
        sheet.usedRange().style({
          fontFamily: 'Arial',
          border: 'thin',
        });
        sheet.row(5).height(50);
        sheet.row(7).height(20);
        sheet.cell('A5').style({
          bold: 'true',
          border: 'none',
          fontSize: '13',
          horizontalAlignment: 'center',
          verticalAlignment: 'center',
        });

        centerColumns.forEach(col => {
          sheet.column(col).style({
            horizontalAlignment: 'center',
            verticalAlignment: 'center',
          });
        });

        hiddenRows.forEach(rowNumber => {
          sheet.row(rowNumber).hidden(true);
        });

        hiddenColumns.forEach(columnName => {
          sheet.column(columnName).hidden(true);
        });

        getDummyArray(startGenTitleOfColumn).forEach(element => {
          getDummyArray(ColumnsIndex).forEach(item => {
            sheet
              .row(element.key + 1)
              .cell(item.key + 1)
              .style({
                border: 'none',
              });
          });
        });
        getDummyArray(ColumnsIndex).forEach(item => {
          sheet
            .row(7)
            .cell(item.key + 1)
            .style({
              fill: '93C47D',
              bold: 'true',
              fontSize: '9',
              horizontalAlignment: 'center',
              verticalAlignment: 'center',
            });
        });
      });
      return workbook
        .outputAsync()
        .then(newWorkBookBlob => URL.createObjectURL(newWorkBookBlob));
    });

  // create new work book
  const workbook = XLSX.utils.book_new();

  // create empty worksheet
  const worksheet = XLSX.utils.json_to_sheet([]);

  // add worksheet into work book
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Trang tính 1');

  worksheet['!merges'] = [{ s: { c: 0, r: 4 }, e: { c: 8, r: 4 } }];

  XLSX.utils.sheet_add_aoa(worksheet, [[...ColumnCheck]], {
    origin: 'A1',
    cellStyles: true,
  });

  XLSX.utils.sheet_add_aoa(worksheet, [['NHẬP KHẨU HÀNG BÁN']], {
    origin: 'A5',
    cellStyles: true,
  });

  XLSX.utils.sheet_add_aoa(worksheet, [[...TitleOfColumnKey]], {
    origin: 'A6',
    cellStyles: true,
  });

  XLSX.utils.sheet_add_aoa(worksheet, [[...TitleOfColumn]], {
    origin: 'A7',
    cellStyles: true,
  });

  XLSX.utils.sheet_add_json(worksheet, billItems, {
    origin: 'A8',
    skipHeader: true,
  });

  const colWidths = [
    { wch: 20 },
    { wch: 15 },
    { wch: 25 },
    { wch: 15 },
    { wch: 15 },
    { wch: 15 },
    { wch: 20 },
    { wch: 15 },
    { wch: 15 },
  ];

  // add cols width
  worksheet['!cols'] = colWidths;

  const workBookBlob = convertWorkBookToBlob(workbook);

  return addStyles(workBookBlob);
};

export const handleExportBizmanOrder = (orderItems: any[]): any => {
  const TitleOfColumn = [
    'Mã hàng',
    'Tên hàng',
    'Số lô',
    'Exp Date',
    'SL',
    'Đơn giá',
    'CK(%)',
    'Ghi chú',
    'TT không VAT',
    'Thuế VND',
  ];

  const leftColumns = ['B', 'H'];
  const rightColumns = ['A', 'C', 'D', 'E', 'F', 'G', 'I', 'J'];
  const defaultTitleOfColumnLength = TitleOfColumn.length;
  const startGenTitleOfColumn = 6;

  const addStyles = (workbookBlob): string =>
    XlsxPopulate.fromDataAsync(workbookBlob).then(workbook => {
      const ColumnsIndex = defaultTitleOfColumnLength;
      workbook.sheets().forEach(sheet => {
        sheet.usedRange().style({
          fontFamily: 'Arial',
          border: 'thin',
        });
        leftColumns.forEach(col => {
          sheet.column(col).style({
            horizontalAlignment: 'left',
            verticalAlignment: 'left',
          });
        });
        rightColumns.forEach(col => {
          sheet.column(col).style({
            horizontalAlignment: 'right',
            verticalAlignment: 'right',
          });
        });

        getDummyArray(startGenTitleOfColumn).forEach(element => {
          getDummyArray(ColumnsIndex).forEach(item => {
            sheet.row(element.key + 1).cell(item.key + 1);
          });
        });

        sheet.cell('A1').style({
          horizontalAlignment: 'left',
          verticalAlignment: 'center',
        });
        sheet.cell('B1').style({
          horizontalAlignment: 'left',
          verticalAlignment: 'center',
        });
        sheet.cell('C1').style({
          horizontalAlignment: 'left',
          verticalAlignment: 'center',
        });
        sheet.cell('D1').style({
          horizontalAlignment: 'left',
          verticalAlignment: 'center',
        });
        sheet.cell('E1').style({
          horizontalAlignment: 'right',
          verticalAlignment: 'center',
        });
        sheet.cell('F1').style({
          horizontalAlignment: 'right',
          verticalAlignment: 'center',
        });
        sheet.cell('G1').style({
          horizontalAlignment: 'right',
          verticalAlignment: 'center',
        });
        sheet.cell('H1').style({
          horizontalAlignment: 'left',
          verticalAlignment: 'center',
        });
        sheet.cell('I1').style({
          horizontalAlignment: 'right',
          verticalAlignment: 'center',
        });
        sheet.cell('J1').style({
          horizontalAlignment: 'right',
          verticalAlignment: 'center',
        });
      });
      return workbook
        .outputAsync()
        .then(newWorkBookBlob => URL.createObjectURL(newWorkBookBlob));
    });

  // create new work book
  const workbook = XLSX.utils.book_new();

  // create empty worksheet
  const worksheet = XLSX.utils.json_to_sheet([]);

  // add worksheet into work book
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');

  XLSX.utils.sheet_add_aoa(worksheet, [[...TitleOfColumn]], {
    origin: 'A1',
    cellStyles: true,
  });

  XLSX.utils.sheet_add_json(worksheet, orderItems, {
    origin: 'A2',
    skipHeader: true,
  });

  const colWidths = [
    { wch: 15 },
    { wch: 25 },
    { wch: 10 },
    { wch: 10 },
    { wch: 15 },
    { wch: 15 },
    { wch: 10 },
    { wch: 15 },
    { wch: 15 },
    { wch: 15 },
  ];

  // add cols width
  worksheet['!cols'] = colWidths;

  const workBookBlob = convertWorkBookToBlob(workbook);

  return addStyles(workBookBlob);
};

export const handleExportProofWithBillItem = ({ list, info }): any => {
  const TitleOfColumn = [
    'Ngày',
    'Số chứng từ',
    'Diễn giải',
    'Tên hàng hóa',
    'Đơn vị',
    'Số lượng',
    'Đơn giá',
    'Tiền hàng',
    'Khuyến mại',
    'Tổng',
    'Đã thu',
    'Nợ tăng',
    'Nợ giảm',
    'Dư nợ kì',
  ];

  const centerColumns = ['A'];
  const leftColumns = ['B', 'C', 'D'];
  const rightColumns = ['E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N'];
  const moneyFormatColumns = ['G', 'H', 'I', 'J', 'K', 'L', 'M', 'N'];

  const totalColumns = TitleOfColumn.length;
  const startGenTitleOfColumn = 8;

  const addStyles = (workbookBlob): string =>
    XlsxPopulate.fromDataAsync(workbookBlob).then(workbook => {
      workbook.sheets().forEach(sheet => {
        sheet.usedRange().style({
          fontFamily: 'Times New Roman',
          border: 'thin',
        });
        sheet.row(4).height(20);
        sheet.cell('A4').style({
          bold: 'true',
          border: 'none',
          fontSize: '13',
          horizontalAlignment: 'center',
          verticalAlignment: 'center',
        });

        centerColumns.forEach(col => {
          sheet.column(col).style({
            horizontalAlignment: 'center',
            verticalAlignment: 'center',
          });
        });
        leftColumns.forEach(col => {
          sheet.column(col).style({
            horizontalAlignment: 'left',
            verticalAlignment: 'center',
          });
        });
        rightColumns.forEach(col => {
          sheet.column(col).style({
            horizontalAlignment: 'right',
            verticalAlignment: 'center',
          });
        });

        // * Format tiền
        rightColumns
          .filter(col => moneyFormatColumns.includes(col))
          .forEach(col => {
            sheet.column(col).style({
              horizontalAlignment: 'right',
              verticalAlignment: 'center',
              numberFormat: '#,##0',
            });
          });

        sheet.cell('A1').style({
          bold: 'true',
          horizontalAlignment: 'left',
          verticalAlignment: 'center',
        });
        sheet.cell('A2').style({
          horizontalAlignment: 'left',
          verticalAlignment: 'center',
        });
        sheet.cell('A3').style({
          horizontalAlignment: 'left',
          verticalAlignment: 'center',
        });
        sheet.cell('A5').style({
          italic: 'true',
        });
        sheet.cell('A6').style({
          italic: 'true',
        });

        getDummyArray(startGenTitleOfColumn - 1).forEach(element => {
          getDummyArray(totalColumns).forEach(item => {
            sheet
              .row(element.key + 1)
              .cell(item.key + 1)
              .style({
                border: 'none',
              });
          });
        });

        getDummyArray(totalColumns).forEach(item => {
          sheet
            .row(startGenTitleOfColumn)
            .cell(item.key + 1)
            .style({
              fill: '93C47D',
              bold: 'true',
              verticalAlignment: 'center',
            });
        });
        getDummyArray(totalColumns).forEach(item => {
          sheet
            .row(startGenTitleOfColumn + list.length + 1)
            .cell(item.key + 1)
            .style({
              fill: '93C47D',
              bold: 'true',
              horizontalAlignment: 'right',
              verticalAlignment: 'center',
            });
        });

        getDummyArray(5).forEach(item => {
          sheet.row(startGenTitleOfColumn + list.length + item.key + 2).style({
            border: 'none',
            bold: 'true',
            horizontalAlignment: 'left',
            verticalAlignment: 'center',
          });
        });

        for (let i = 0; i < list.length; i += 1) {
          if (list[i].createdAt === 'Cộng') {
            getDummyArray(totalColumns).forEach(item => {
              sheet
                .row(8 + i + 2)
                .cell(item.key + 2)
                .style({
                  bold: 'true',
                  horizontalAlignment: 'right',
                  verticalAlignment: 'center',
                });
              sheet
                .row(8 + i + 2)
                .cell(1)
                .style({
                  bold: 'true',
                  verticalAlignment: 'center',
                });
            });
          }
        }
      });
      return workbook
        .outputAsync()
        .then(newWorkBookBlob => URL.createObjectURL(newWorkBookBlob));
    });

  // create new work book
  const workbook = XLSX.utils.book_new();
  // create empty worksheet
  const worksheet = XLSX.utils.json_to_sheet([]);

  // add worksheet into work book
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Trang tính 1');

  // * Merge cột lại với nhau cho title
  const merge = [
    { s: { c: 0, r: 3 }, e: { c: TitleOfColumn.length - 1, r: 3 } },
    { s: { c: 0, r: 4 }, e: { c: TitleOfColumn.length - 1, r: 4 } },
    { s: { c: 0, r: 5 }, e: { c: TitleOfColumn.length - 1, r: 5 } },

    ...TitleOfColumn.map((item: any, index: number) => {
      return { s: { c: index, r: 7 }, e: { c: index, r: 8 } };
    }),
  ];

  worksheet['!merges'] = merge;

  XLSX.utils.sheet_add_aoa(worksheet, [[info.supplierName]], {
    origin: 'A1',
    cellStyles: true,
  });
  XLSX.utils.sheet_add_aoa(worksheet, [[info.supplierAddress]], {
    origin: 'A2',
    cellStyles: true,
  });
  XLSX.utils.sheet_add_aoa(worksheet, [[info.supplierPhoneNumber]], {
    origin: 'A3',
    cellStyles: true,
  });

  XLSX.utils.sheet_add_aoa(worksheet, [[info.title]], {
    origin: 'A4',
    cellStyles: true,
  });

  XLSX.utils.sheet_add_aoa(worksheet, [[info.time]], {
    origin: 'A5',
    cellStyles: true,
  });

  XLSX.utils.sheet_add_aoa(worksheet, [[info.customerInfo]], {
    origin: 'A6',
    cellStyles: true,
  });

  XLSX.utils.sheet_add_aoa(worksheet, [[...TitleOfColumn]], {
    origin: 'A8',
    cellStyles: true,
  });

  XLSX.utils.sheet_add_json(worksheet, list, {
    origin: 'A10',
    skipHeader: true,
  });

  XLSX.utils.sheet_add_aoa(
    worksheet,
    [[info.startDebt.name, '', info.startDebt.value]],
    {
      origin: `A${8 + list.length + 3}`,
      cellStyles: false,
    }
  );
  XLSX.utils.sheet_add_aoa(
    worksheet,
    [[info.debitUp.name, '', info.debitUp.value]],
    {
      origin: `A${8 + list.length + 4}`,
      cellStyles: false,
    }
  );
  XLSX.utils.sheet_add_aoa(
    worksheet,
    [[info.debitDown.name, '', info.debitDown.value]],
    {
      origin: `A${8 + list.length + 5}`,
      cellStyles: false,
    }
  );
  XLSX.utils.sheet_add_aoa(
    worksheet,
    [[info.remainAmount.name, '', info.remainAmount.value]],
    {
      origin: `A${8 + list.length + 6}`,
      cellStyles: false,
    }
  );

  const colWidths = [
    { wch: 15 },
    { wch: 25 },
    { wch: 25 },
    { wch: 25 },
    { wch: 20 },
    { wch: 15 },
    { wch: 15 },
    { wch: 15 },
    { wch: 15 },
    { wch: 15 },
    { wch: 15 },
    { wch: 15 },
    { wch: 15 },
    { wch: 15 },
  ];

  // add cols width
  worksheet['!cols'] = colWidths;

  const workBookBlob = convertWorkBookToBlob(workbook);

  return addStyles(workBookBlob);
};

export const handleExportPaymentBill = ({
  list,
  title,
  info,
  paymentInfo,
  deliveryInfo,
  summaryInfo,
  categoryIndex,
}): any => {
  const TitleOfColumn = title;
  const col = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L'];
  const leftColumns = title.includes('Ghi chú')
    ? ['B', col[title.findIndex(item => item == 'Ghi chú')]]
    : ['B'];
  const rightColumns = ['E', 'F', 'G', 'H', 'I', 'J', 'K', 'L'];

  const totalColumns = TitleOfColumn.length;
  const startGenTitleOfColumn = 8;

  const addStyles = (workbookBlob): string =>
    XlsxPopulate.fromDataAsync(workbookBlob).then(workbook => {
      workbook.sheets().forEach(sheet => {
        sheet.usedRange().style({
          fontFamily: 'Arial',
          border: 'thin',
          horizontalAlignment: 'center',
          verticalAlignment: 'center',
          wrapText: true,
        });
        rightColumns.forEach(col => {
          sheet.column(col).style({
            horizontalAlignment: 'right',
            verticalAlignment: 'center',
          });
        });
        leftColumns.forEach(col => {
          sheet.column(col).style({
            horizontalAlignment: 'left',
            verticalAlignment: 'center',
          });
        });
        sheet.row(2).height(20);
        sheet.row(4).style({
          fontSize: '12',
          horizontalAlignment: 'left',
          verticalAlignment: 'center',
        });
        sheet.row(5).style({
          fontSize: '12',
          horizontalAlignment: 'left',
          verticalAlignment: 'center',
        });
        sheet.row(6).style({
          fontSize: '12',
          horizontalAlignment: 'left',
          verticalAlignment: 'center',
        });
        sheet.row(7).style({
          fontSize: '12',
          horizontalAlignment: 'left',
          verticalAlignment: 'center',
        });
        sheet.cell('A2').style({
          bold: 'true',
          border: 'none',
          fontSize: '13',
          horizontalAlignment: 'center',
          verticalAlignment: 'center',
        });

        getDummyArray(startGenTitleOfColumn - 1).forEach(element => {
          getDummyArray(totalColumns).forEach(item => {
            sheet
              .row(element.key + 1)
              .cell(item.key + 1)
              .style({
                border: 'none',
                wrapText: true,
              });
          });
        });

        getDummyArray(totalColumns).forEach(item => {
          sheet
            .row(startGenTitleOfColumn)
            .cell(item.key + 1)
            .style({
              fill: '93C47D',
              bold: 'true',
              verticalAlignment: 'center',
              wrapText: true,
            });
        });

        for (let i = 0; i < list.length; i += 1) {
          if (list[i].name === 'Tổng') {
            getDummyArray(totalColumns).forEach(item => {
              sheet
                .row(7 + i + 1)
                .cell(item.key + 1)
                .style({
                  fill: 'e7f5e6',
                  bold: 'true',
                  wrapText: true,
                });
            });
          }
        }

        categoryIndex.forEach((category: number) => {
          getDummyArray(totalColumns).forEach(item => {
            sheet
              .row(8 + category)
              .cell(item.key + 1)
              .style({
                fill: 'EAF4E6',
                bold: 'true',
                border: 'thin',
                wrapText: true,
                horizontalAlignment: 'left',
              });
          });
        });

        paymentInfo.forEach((item: any, index: number) => {
          sheet.cell(`A${list.length + 10 + index}`).style({
            fontSize: '12',
            horizontalAlignment: 'left',
            verticalAlignment: 'center',
          });
        });

        deliveryInfo.forEach((item: any, index: number) => {
          sheet.cell(`C${list.length + 10 + index}`).style({
            fontSize: '12',
            horizontalAlignment: 'left',
            verticalAlignment: 'center',
          });
        });

        summaryInfo.forEach((item: any, index: number) => {
          sheet
            .cell(`${col[title.length - 1]}${list.length + 10 + index}`)
            .style({
              fontSize: '12',
              horizontalAlignment: 'right',
              verticalAlignment: 'center',
            });
        });

        summaryInfo.forEach((item: any, index: number) => {
          sheet
            .cell(`${col[title.length - 2]}${list.length + 10 + index}`)
            .style({
              fontSize: '12',
              horizontalAlignment: 'left',
              verticalAlignment: 'center',
            });
        });
      });
      return workbook
        .outputAsync()
        .then(newWorkBookBlob => URL.createObjectURL(newWorkBookBlob));
    });

  // create new work book
  const workbook = XLSX.utils.book_new();
  // create empty worksheet
  const worksheet = XLSX.utils.json_to_sheet([]);

  // add worksheet into work book
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Trang tính 1');

  const merge = [
    { s: { c: 0, r: 1 }, e: { c: title.length - 1, r: 1 } },
    { s: { c: 0, r: 3 }, e: { c: 1, r: 3 } },
    { s: { c: 0, r: 4 }, e: { c: 1, r: 4 } },
    { s: { c: 0, r: 5 }, e: { c: 1, r: 5 } },
    { s: { c: 0, r: 6 }, e: { c: 1, r: 6 } },
    { s: { c: 2, r: 3 }, e: { c: 4, r: 3 } },
    { s: { c: 2, r: 4 }, e: { c: 4, r: 4 } },
    { s: { c: 2, r: 5 }, e: { c: 4, r: 5 } },
    ...title.map((item: any, index: number) => {
      return { s: { c: index, r: 7 }, e: { c: index, r: 7 } };
    }),
    ...categoryIndex.map((element: number) => {
      return {
        s: { c: 0, r: startGenTitleOfColumn + element - 1 },
        e: { c: title.length - 1, r: startGenTitleOfColumn + element - 1 },
      };
    }),
    {
      s: { c: 0, r: startGenTitleOfColumn + list.length },
      e: { c: title.length - 1, r: startGenTitleOfColumn + list.length },
    },
    ...paymentInfo.map((item: any, index: number) => {
      return {
        s: { c: 0, r: list.length + index + startGenTitleOfColumn + 1 },
        e: { c: 1, r: list.length + index + startGenTitleOfColumn + 1 },
      };
    }),
    ...deliveryInfo.map((item: any, index: number) => {
      return {
        s: { c: 2, r: list.length + index + startGenTitleOfColumn + 1 },
        e: { c: 4, r: list.length + index + startGenTitleOfColumn + 1 },
      };
    }),
  ];

  worksheet['!merges'] = merge;

  XLSX.utils.sheet_add_aoa(worksheet, [[info.title]], {
    origin: 'A2',
    cellStyles: true,
  });

  XLSX.utils.sheet_add_aoa(worksheet, [[info.code]], {
    origin: 'A4',
    cellStyles: true,
  });

  XLSX.utils.sheet_add_aoa(worksheet, [[info.employeeName]], {
    origin: 'A5',
    cellStyles: true,
  });

  XLSX.utils.sheet_add_aoa(worksheet, [[info.customerName]], {
    origin: 'A6',
    cellStyles: true,
  });

  if (info?.address) {
    XLSX.utils.sheet_add_aoa(worksheet, [[info.address]], {
      origin: 'A7',
      cellStyles: true,
    });
  }

  XLSX.utils.sheet_add_aoa(worksheet, [[info.createdAt]], {
    origin: 'C4',
    cellStyles: true,
  });

  XLSX.utils.sheet_add_aoa(worksheet, [[info.cashier]], {
    origin: 'C5',
    cellStyles: true,
  });

  XLSX.utils.sheet_add_aoa(worksheet, [[info.phoneNumber]], {
    origin: 'C6',
    cellStyles: true,
  });

  XLSX.utils.sheet_add_aoa(worksheet, [[...TitleOfColumn]], {
    origin: 'A8',
    cellStyles: true,
  });

  XLSX.utils.sheet_add_json(worksheet, list, {
    origin: 'A9',
    skipHeader: true,
  });

  // render payment info

  paymentInfo.forEach((item: any, index: number) => {
    XLSX.utils.sheet_add_aoa(worksheet, [[item]], {
      origin: `A${list.length + 10 + index}`,
      cellStyles: true,
    });
  });

  deliveryInfo.forEach((item: any, index: number) => {
    XLSX.utils.sheet_add_aoa(worksheet, [[item]], {
      origin: `C${list.length + 10 + index}`,
      cellStyles: true,
    });
  });

  summaryInfo.forEach((item: any, index: number) => {
    XLSX.utils.sheet_add_aoa(worksheet, [[item.label]], {
      origin: `${col[title.length - 2]}${list.length + 10 + index}`,
      cellStyles: true,
    });
  });

  summaryInfo.forEach((item: any, index: number) => {
    XLSX.utils.sheet_add_aoa(worksheet, [[item.value]], {
      origin: `${col[title.length - 1]}${list.length + 10 + index}`,
      cellStyles: true,
    });
  });

  const colWidths = [
    { wch: 8 },
    { wch: 48 },
    ...title.slice(2).map((item: any) => {
      return { wch: 15 };
    }),
  ];

  // add cols width
  worksheet['!cols'] = colWidths;

  const workBookBlob = convertWorkBookToBlob(workbook);

  return addStyles(workBookBlob);
};

export const handleExportPurchaseBill = ({
  list,
  title,
  info,
  categoryIndex,
}): any => {
  const TitleOfColumn = title;
  const leftColumns = ['B', 'C'];

  const totalColumns = TitleOfColumn.length;
  const startGenTitleOfColumn = 8;

  const addStyles = (workbookBlob): string =>
    XlsxPopulate.fromDataAsync(workbookBlob).then(workbook => {
      workbook.sheets().forEach(sheet => {
        sheet.usedRange().style({
          fontFamily: 'Arial',
          border: 'thin',
          horizontalAlignment: 'center',
          verticalAlignment: 'center',
          wrapText: true,
        });
        leftColumns.forEach(col => {
          sheet.column(col).style({
            horizontalAlignment: 'left',
            verticalAlignment: 'center',
          });
        });
        sheet.row(4).height(20);
        sheet.cell('A3').style({
          bold: 'true',
          border: 'none',
          fontSize: '13',
          horizontalAlignment: 'center',
          verticalAlignment: 'center',
        });

        sheet.cell('A4').style({
          bold: 'true',
          horizontalAlignment: 'center',
          verticalAlignment: 'center',
        });
        sheet.cell('A5').style({
          horizontalAlignment: 'left',
          verticalAlignment: 'center',
        });
        sheet.cell('A6').style({
          horizontalAlignment: 'left',
          verticalAlignment: 'center',
        });

        getDummyArray(startGenTitleOfColumn - 1).forEach(element => {
          getDummyArray(totalColumns).forEach(item => {
            sheet
              .row(element.key + 1)
              .cell(item.key + 1)
              .style({
                border: 'none',
                wrapText: true,
              });
          });
        });

        getDummyArray(totalColumns).forEach(item => {
          sheet
            .row(startGenTitleOfColumn)
            .cell(item.key + 1)
            .style({
              fill: '93C47D',
              bold: 'true',
              verticalAlignment: 'center',
              wrapText: true,
            });
        });

        for (let i = 0; i < list.length; i += 1) {
          if (list[i].name === 'Tổng') {
            getDummyArray(totalColumns).forEach(item => {
              sheet
                .row(8 + i + 2)
                .cell(item.key + 1)
                .style({
                  fill: 'e7f5e6',
                  bold: 'true',
                  wrapText: true,
                });
            });
          }
        }

        categoryIndex.forEach((category: number) => {
          getDummyArray(totalColumns).forEach(item => {
            sheet
              .row(8 + category + 1)
              .cell(item.key + 1)
              .style({
                fill: 'EAF4E6',
                bold: 'true',
                border: 'thin',
                wrapText: true,
                horizontalAlignment: 'left',
              });
          });
        });

        sheet.cell(`A${list.length + 10}`).style({
          border: 'thin',
          horizontalAlignment: 'left',
          verticalAlignment: 'top',
        });

        sheet.cell(`A${list.length + 12}`).style({
          border: 'thin',
          horizontalAlignment: 'left',
          verticalAlignment: 'top',
        });

        sheet.cell(`A${list.length + 14}`).style({
          border: 'thin',
          horizontalAlignment: 'left',
          verticalAlignment: 'top',
        });

        sheet.cell(`A${list.length + 16}`).style({
          border: 'thin',
          horizontalAlignment: 'left',
          verticalAlignment: 'top',
        });
      });
      return workbook
        .outputAsync()
        .then(newWorkBookBlob => URL.createObjectURL(newWorkBookBlob));
    });

  // create new work book
  const workbook = XLSX.utils.book_new();
  // create empty worksheet
  const worksheet = XLSX.utils.json_to_sheet([]);

  // add worksheet into work book
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Trang tính 1');

  const merge = [
    { s: { c: 0, r: 2 }, e: { c: title.length - 1, r: 2 } },
    { s: { c: 0, r: 3 }, e: { c: title.length - 1, r: 3 } },
    { s: { c: 0, r: 4 }, e: { c: 1, r: 4 } },
    { s: { c: 0, r: 5 }, e: { c: 1, r: 5 } },
    ...title.map((item: any, index: number) => {
      return { s: { c: index, r: 7 }, e: { c: index, r: 8 } };
    }),
    ...categoryIndex.map((element: number) => {
      return {
        s: { c: 0, r: startGenTitleOfColumn + element },
        e: { c: title.length - 1, r: startGenTitleOfColumn + element },
      };
    }),
    {
      s: { c: 0, r: list.length + 9 },
      e: { c: title.length - 1, r: list.length + 10 },
    },
    {
      s: { c: 0, r: list.length + 11 },
      e: { c: title.length - 1, r: list.length + 12 },
    },
    {
      s: { c: 0, r: list.length + 13 },
      e: { c: title.length - 1, r: list.length + 14 },
    },
    {
      s: { c: 0, r: list.length + 15 },
      e: { c: title.length - 1, r: list.length + 16 },
    },
  ];

  worksheet['!merges'] = merge;

  XLSX.utils.sheet_add_aoa(worksheet, [[info.title]], {
    origin: 'A3',
    cellStyles: true,
  });

  XLSX.utils.sheet_add_aoa(worksheet, [[info.date]], {
    origin: 'A4',
    cellStyles: true,
  });

  XLSX.utils.sheet_add_aoa(worksheet, [[info.customer]], {
    origin: 'A5',
    cellStyles: true,
  });

  XLSX.utils.sheet_add_aoa(worksheet, [[info.supplier]], {
    origin: 'A6',
    cellStyles: true,
  });

  XLSX.utils.sheet_add_aoa(worksheet, [[...TitleOfColumn]], {
    origin: 'A8',
    cellStyles: true,
  });

  XLSX.utils.sheet_add_json(worksheet, list, {
    origin: 'A10',
    skipHeader: true,
  });

  XLSX.utils.sheet_add_aoa(worksheet, [[info.customerNote]], {
    origin: `A${list.length + 10}`,
    cellStyles: true,
  });

  XLSX.utils.sheet_add_aoa(worksheet, [[info.supplierNote]], {
    origin: `A${list.length + 12}`,
    cellStyles: true,
  });

  XLSX.utils.sheet_add_aoa(worksheet, [[info.delivery]], {
    origin: `A${list.length + 14}`,
    cellStyles: true,
  });

  XLSX.utils.sheet_add_aoa(worksheet, [[info.received]], {
    origin: `A${list.length + 16}`,
    cellStyles: true,
  });

  const colWidths = [
    { wch: 5 },
    { wch: 18 },
    { wch: 48 },
    ...title.slice(2).map((item: any) => {
      return { wch: 15 };
    }),
  ];

  // add cols width
  worksheet['!cols'] = colWidths;

  const workBookBlob = convertWorkBookToBlob(workbook);

  return addStyles(workBookBlob);
};

export const handleExportQuantityReport = ({
  list,
  titles,
  info,
  supplierIndexes,
}): any => {
  const TitleOfColumns = titles.map(item => item.name);
  const allColumns = ['A', 'B', 'C', 'D', 'E', 'F', 'G'];

  const totalColumns = TitleOfColumns.length;
  const startGenTitleOfColumn = 6;

  const addStyles = (workbookBlob): string =>
    XlsxPopulate.fromDataAsync(workbookBlob).then(workbook => {
      workbook.sheets().forEach(sheet => {
        sheet.usedRange().style({
          fontFamily: 'Arial',
          border: 'thin',
          horizontalAlignment: 'center',
          verticalAlignment: 'center',
          wrapText: true,
        });
        allColumns.forEach((col, index) => {
          if (titles[index]?.horizontalAlignment == 'left') {
            sheet.column(col).style({
              horizontalAlignment: 'left',
              verticalAlignment: 'center',
            });
          }
        });
        sheet.row(3).height(20);
        sheet.cell('A3').style({
          bold: 'true',
          border: 'none',
          fontSize: '13',
          horizontalAlignment: 'center',
          verticalAlignment: 'center',
        });
        sheet.cell('A4').style({
          bold: 'true',
          horizontalAlignment: 'center',
          verticalAlignment: 'center',
        });

        getDummyArray(startGenTitleOfColumn - 1).forEach(element => {
          getDummyArray(totalColumns).forEach(item => {
            sheet
              .row(element.key + 1)
              .cell(item.key + 1)
              .style({
                border: 'none',
                wrapText: true,
              });
          });
        });

        getDummyArray(totalColumns).forEach(item => {
          sheet
            .row(startGenTitleOfColumn)
            .cell(item.key + 1)
            .style({
              fill: '93C47D',
              bold: 'true',
              verticalAlignment: 'center',
              wrapText: true,
            });
        });

        supplierIndexes.forEach((supplierIndex: number) => {
          getDummyArray(totalColumns).forEach(item => {
            sheet
              .row(startGenTitleOfColumn + supplierIndex + 1)
              .cell(item.key + 1)
              .style({
                fill: 'EAF4E6',
                bold: 'true',
                border: 'thin',
                wrapText: true,
                horizontalAlignment: 'left',
              });
          });
        });
      });
      return workbook
        .outputAsync()
        .then(newWorkBookBlob => URL.createObjectURL(newWorkBookBlob));
    });

  // create new work book
  const workbook = XLSX.utils.book_new();
  // create empty worksheet
  const worksheet = XLSX.utils.json_to_sheet([]);

  // add worksheet into work book
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Trang tính 1');

  const merge = [
    { s: { c: 0, r: 2 }, e: { c: TitleOfColumns.length - 1, r: 2 } },
    { s: { c: 0, r: 3 }, e: { c: TitleOfColumns.length - 1, r: 3 } },
    ...TitleOfColumns.map((item: any, index: number) => {
      return { s: { c: index, r: 5 }, e: { c: index, r: 6 } };
    }),
    ...supplierIndexes.map((element: number) => {
      return {
        s: { c: 0, r: startGenTitleOfColumn + element },
        e: { c: TitleOfColumns.length - 1, r: startGenTitleOfColumn + element },
      };
    }),
  ];

  worksheet['!merges'] = merge;

  XLSX.utils.sheet_add_aoa(worksheet, [[info.title]], {
    origin: 'A3',
    cellStyles: true,
  });

  XLSX.utils.sheet_add_aoa(worksheet, [[info.date]], {
    origin: 'A4',
    cellStyles: true,
  });

  XLSX.utils.sheet_add_aoa(worksheet, [[...TitleOfColumns]], {
    origin: 'A6',
    cellStyles: true,
  });

  XLSX.utils.sheet_add_json(worksheet, list, {
    origin: 'A8',
    skipHeader: true,
  });

  const colWidths = titles.map(item => {
    return { wch: item.width };
  });

  // add cols width
  worksheet['!cols'] = colWidths;

  const workBookBlob = convertWorkBookToBlob(workbook);

  return addStyles(workBookBlob);
};

export const handleExportProductQuantityReport = ({
  list,
  titles,
  info,
  categoryIndexes,
}): any => {
  const TitleOfColumns = titles.map(item => item.name);
  const allColumns = ['A', 'B', 'C', 'D', 'E', 'F', 'G'];

  const totalColumns = TitleOfColumns.length;
  const startGenTitleOfColumn = 6;

  const addStyles = (workbookBlob): string =>
    XlsxPopulate.fromDataAsync(workbookBlob).then(workbook => {
      workbook.sheets().forEach(sheet => {
        sheet.usedRange().style({
          fontFamily: 'Arial',
          border: 'thin',
          horizontalAlignment: 'center',
          verticalAlignment: 'center',
          wrapText: true,
        });
        allColumns.forEach((col, index) => {
          if (titles[index]?.horizontalAlignment == 'left') {
            sheet.column(col).style({
              horizontalAlignment: 'left',
              verticalAlignment: 'center',
            });
          }
        });
        sheet.row(3).height(20);
        sheet.cell('A3').style({
          bold: 'true',
          border: 'none',
          fontSize: '13',
          horizontalAlignment: 'center',
          verticalAlignment: 'center',
        });
        sheet.cell('A4').style({
          bold: 'true',
          horizontalAlignment: 'center',
          verticalAlignment: 'center',
        });

        getDummyArray(startGenTitleOfColumn - 1).forEach(element => {
          getDummyArray(totalColumns).forEach(item => {
            sheet
              .row(element.key + 1)
              .cell(item.key + 1)
              .style({
                border: 'none',
                wrapText: true,
              });
          });
        });

        getDummyArray(totalColumns).forEach(item => {
          sheet
            .row(startGenTitleOfColumn)
            .cell(item.key + 1)
            .style({
              fill: '93C47D',
              bold: 'true',
              verticalAlignment: 'center',
              wrapText: true,
            });
        });

        categoryIndexes.forEach((supplierIndex: number) => {
          getDummyArray(totalColumns).forEach(item => {
            sheet
              .row(startGenTitleOfColumn + supplierIndex + 1)
              .cell(item.key + 1)
              .style({
                fill: 'EAF4E6',
                bold: 'true',
                border: 'thin',
                wrapText: true,
                horizontalAlignment: 'left',
              });
          });
        });
      });
      return workbook
        .outputAsync()
        .then(newWorkBookBlob => URL.createObjectURL(newWorkBookBlob));
    });

  // create new work book
  const workbook = XLSX.utils.book_new();
  // create empty worksheet
  const worksheet = XLSX.utils.json_to_sheet([]);

  // add worksheet into work book
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Trang tính 1');

  const merge = [
    { s: { c: 0, r: 2 }, e: { c: TitleOfColumns.length - 1, r: 2 } },
    { s: { c: 0, r: 3 }, e: { c: TitleOfColumns.length - 1, r: 3 } },
    ...TitleOfColumns.map((item: any, index: number) => {
      return { s: { c: index, r: 5 }, e: { c: index, r: 6 } };
    }),
    ...categoryIndexes.map((element: number) => {
      return {
        s: { c: 0, r: startGenTitleOfColumn + element },
        e: { c: TitleOfColumns.length - 1, r: startGenTitleOfColumn + element },
      };
    }),
  ];

  worksheet['!merges'] = merge;

  XLSX.utils.sheet_add_aoa(worksheet, [[info.title]], {
    origin: 'A3',
    cellStyles: true,
  });

  XLSX.utils.sheet_add_aoa(worksheet, [[info.date]], {
    origin: 'A4',
    cellStyles: true,
  });

  XLSX.utils.sheet_add_aoa(worksheet, [[...TitleOfColumns]], {
    origin: 'A6',
    cellStyles: true,
  });

  XLSX.utils.sheet_add_json(worksheet, list, {
    origin: 'A8',
    skipHeader: true,
  });

  const colWidths = titles.map(item => {
    return { wch: item.width };
  });

  // add cols width
  worksheet['!cols'] = colWidths;

  const workBookBlob = convertWorkBookToBlob(workbook);

  return addStyles(workBookBlob);
};

export const handleExportWarehouseBill = ({
  list,
  title,
  info,
  categoryIndex,
}): any => {
  const TitleOfColumn = title;
  const leftColumns = ['B', 'C'];

  const totalColumns = TitleOfColumn.length;
  const startGenTitleOfColumn = 8;

  const addStyles = (workbookBlob): string =>
    XlsxPopulate.fromDataAsync(workbookBlob).then(workbook => {
      workbook.sheets().forEach(sheet => {
        sheet.usedRange().style({
          fontFamily: 'Arial',
          border: 'thin',
          horizontalAlignment: 'center',
          verticalAlignment: 'center',
          wrapText: true,
        });
        leftColumns.forEach(col => {
          sheet.column(col).style({
            horizontalAlignment: 'left',
            verticalAlignment: 'center',
          });
        });
        sheet.row(4).height(20);
        sheet.cell('A3').style({
          bold: 'true',
          border: 'none',
          fontSize: '13',
          horizontalAlignment: 'center',
          verticalAlignment: 'center',
        });

        sheet.cell('A4').style({
          bold: 'true',
          horizontalAlignment: 'center',
          verticalAlignment: 'center',
        });
        sheet.cell('A5').style({
          horizontalAlignment: 'left',
          verticalAlignment: 'center',
        });
        sheet.cell('A6').style({
          horizontalAlignment: 'left',
          verticalAlignment: 'center',
        });

        getDummyArray(startGenTitleOfColumn - 1).forEach(element => {
          getDummyArray(totalColumns).forEach(item => {
            sheet
              .row(element.key + 1)
              .cell(item.key + 1)
              .style({
                border: 'none',
                wrapText: true,
              });
          });
        });

        getDummyArray(totalColumns).forEach(item => {
          sheet
            .row(startGenTitleOfColumn)
            .cell(item.key + 1)
            .style({
              fill: '93C47D',
              bold: 'true',
              verticalAlignment: 'center',
              wrapText: true,
            });
        });

        for (let i = 0; i < list.length; i += 1) {
          if (list[i].name === 'Tổng') {
            getDummyArray(totalColumns).forEach(item => {
              sheet
                .row(8 + i + 2)
                .cell(item.key + 1)
                .style({
                  fill: 'e7f5e6',
                  bold: 'true',
                  wrapText: true,
                });
            });
          }
        }

        categoryIndex.forEach((category: number) => {
          getDummyArray(totalColumns).forEach(item => {
            sheet
              .row(8 + category + 1)
              .cell(item.key + 1)
              .style({
                fill: 'EAF4E6',
                bold: 'true',
                border: 'thin',
                wrapText: true,
                horizontalAlignment: 'left',
              });
          });
        });

        sheet.cell(`A${list.length + 10}`).style({
          border: 'thin',
          horizontalAlignment: 'left',
          verticalAlignment: 'top',
        });

        sheet.cell(`A${list.length + 12}`).style({
          border: 'thin',
          horizontalAlignment: 'left',
          verticalAlignment: 'top',
        });

        sheet.cell(`A${list.length + 14}`).style({
          border: 'thin',
          horizontalAlignment: 'left',
          verticalAlignment: 'top',
        });

        sheet.cell(`A${list.length + 16}`).style({
          border: 'thin',
          horizontalAlignment: 'left',
          verticalAlignment: 'top',
        });
      });
      return workbook
        .outputAsync()
        .then(newWorkBookBlob => URL.createObjectURL(newWorkBookBlob));
    });

  // create new work book
  const workbook = XLSX.utils.book_new();
  // create empty worksheet
  const worksheet = XLSX.utils.json_to_sheet([]);

  // add worksheet into work book
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Trang tính 1');

  const merge = [
    { s: { c: 0, r: 2 }, e: { c: title.length - 1, r: 2 } },
    { s: { c: 0, r: 3 }, e: { c: title.length - 1, r: 3 } },
    { s: { c: 0, r: 4 }, e: { c: 1, r: 4 } },
    { s: { c: 0, r: 5 }, e: { c: 1, r: 5 } },
    ...title.map((item: any, index: number) => {
      return { s: { c: index, r: 7 }, e: { c: index, r: 8 } };
    }),
    ...categoryIndex.map((element: number) => {
      return {
        s: { c: 0, r: startGenTitleOfColumn + element },
        e: { c: title.length - 1, r: startGenTitleOfColumn + element },
      };
    }),
    {
      s: { c: 0, r: list.length + 9 },
      e: { c: title.length - 1, r: list.length + 10 },
    },
    {
      s: { c: 0, r: list.length + 11 },
      e: { c: title.length - 1, r: list.length + 12 },
    },
    {
      s: { c: 0, r: list.length + 13 },
      e: { c: title.length - 1, r: list.length + 14 },
    },
    {
      s: { c: 0, r: list.length + 15 },
      e: { c: title.length - 1, r: list.length + 16 },
    },
  ];

  worksheet['!merges'] = merge;

  XLSX.utils.sheet_add_aoa(worksheet, [[info.title]], {
    origin: 'A3',
    cellStyles: true,
  });

  XLSX.utils.sheet_add_aoa(worksheet, [[info.date]], {
    origin: 'A4',
    cellStyles: true,
  });

  XLSX.utils.sheet_add_aoa(worksheet, [[info.customer]], {
    origin: 'A5',
    cellStyles: true,
  });

  XLSX.utils.sheet_add_aoa(worksheet, [[info.supplier]], {
    origin: 'A6',
    cellStyles: true,
  });

  XLSX.utils.sheet_add_aoa(worksheet, [[...TitleOfColumn]], {
    origin: 'A8',
    cellStyles: true,
  });

  XLSX.utils.sheet_add_json(worksheet, list, {
    origin: 'A10',
    skipHeader: true,
  });

  XLSX.utils.sheet_add_aoa(worksheet, [[info.customerNote]], {
    origin: `A${list.length + 10}`,
    cellStyles: true,
  });

  XLSX.utils.sheet_add_aoa(worksheet, [[info.supplierNote]], {
    origin: `A${list.length + 12}`,
    cellStyles: true,
  });

  XLSX.utils.sheet_add_aoa(worksheet, [[info.delivery]], {
    origin: `A${list.length + 14}`,
    cellStyles: true,
  });

  XLSX.utils.sheet_add_aoa(worksheet, [[info.received]], {
    origin: `A${list.length + 16}`,
    cellStyles: true,
  });

  const colWidths = [
    { wch: 5 },
    { wch: 18 },
    { wch: 48 },
    ...title.slice(2).map((item: any) => {
      return { wch: 15 };
    }),
  ];

  // add cols width
  worksheet['!cols'] = colWidths;

  const workBookBlob = convertWorkBookToBlob(workbook);

  return addStyles(workBookBlob);
};

export const handleExportOrderProducts = ({
  list,
  title,
  total,
  info,
  categoryIndex,
}): any => {
  const TitleOfColumn = title;
  const leftColumns = title.includes('Mã SKU') ? ['A', 'B'] : ['A'];

  const totalColumns = TitleOfColumn.length;
  const startGenTitleOfColumn = 8;

  const addStyles = (workbookBlob): string =>
    XlsxPopulate.fromDataAsync(workbookBlob).then(workbook => {
      workbook.sheets().forEach(sheet => {
        sheet.usedRange().style({
          fontFamily: 'Arial',
          border: 'thin',
          horizontalAlignment: 'center',
          verticalAlignment: 'center',
          wrapText: true,
        });
        leftColumns.forEach(col => {
          sheet.column(col).style({
            horizontalAlignment: 'left',
            verticalAlignment: 'center',
          });
        });
        sheet.row(4).height(20);
        sheet.cell('A4').style({
          bold: 'true',
          border: 'none',
          fontSize: '13',
          horizontalAlignment: 'center',
          verticalAlignment: 'center',
        });

        sheet.cell('A5').style({
          italic: 'true',
          horizontalAlignment: 'center',
          verticalAlignment: 'center',
        });
        sheet.cell('A6').style({
          italic: 'true',
          horizontalAlignment: 'center',
          verticalAlignment: 'center',
        });

        getDummyArray(startGenTitleOfColumn - 1).forEach(element => {
          getDummyArray(totalColumns).forEach(item => {
            sheet
              .row(element.key + 1)
              .cell(item.key + 1)
              .style({
                border: 'none',
                wrapText: true,
              });
          });
        });

        getDummyArray(totalColumns).forEach(item => {
          sheet
            .row(startGenTitleOfColumn)
            .cell(item.key + 1)
            .style({
              fill: '93C47D',
              bold: 'true',
              verticalAlignment: 'center',
              wrapText: true,
            });
        });

        for (let i = 0; i < list.length; i += 1) {
          if (list[i].name === 'Tổng' || list[i].sku === 'Tổng') {
            getDummyArray(totalColumns).forEach(item => {
              sheet
                .row(8 + i + 2)
                .cell(item.key + 1)
                .style({
                  fill: 'e7f5e6',
                  bold: 'true',
                  wrapText: true,
                });
            });
          }
        }

        categoryIndex.forEach((category: number) => {
          getDummyArray(totalColumns).forEach(item => {
            sheet
              .row(8 + category + 1)
              .cell(item.key + 1)
              .style({
                fill: 'EAF4E6',
                bold: 'true',
                border: 'thin',
                wrapText: true,
                horizontalAlignment: 'left',
              });
          });
        });
      });
      return workbook
        .outputAsync()
        .then(newWorkBookBlob => URL.createObjectURL(newWorkBookBlob));
    });

  // create new work book
  const workbook = XLSX.utils.book_new();
  // create empty worksheet
  const worksheet = XLSX.utils.json_to_sheet([]);

  // add worksheet into work book
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Trang tính 1');

  const merge = [
    { s: { c: 0, r: 3 }, e: { c: title.length - 1, r: 3 } },
    { s: { c: 0, r: 4 }, e: { c: title.length - 1, r: 4 } },
    ...title.map((item: any, index: number) => {
      return { s: { c: index, r: 7 }, e: { c: index, r: 8 } };
    }),
    ...categoryIndex.map((element: number) => {
      return {
        s: { c: 0, r: startGenTitleOfColumn + element },
        e: { c: title.length - 1, r: startGenTitleOfColumn + element },
      };
    }),
  ];

  worksheet['!merges'] = merge;

  XLSX.utils.sheet_add_aoa(worksheet, [[info.title]], {
    origin: 'A4',
    cellStyles: true,
  });

  XLSX.utils.sheet_add_aoa(worksheet, [[info.date]], {
    origin: 'A5',
    cellStyles: true,
  });

  XLSX.utils.sheet_add_aoa(worksheet, [[...TitleOfColumn]], {
    origin: 'A8',
    cellStyles: true,
  });

  XLSX.utils.sheet_add_json(worksheet, list, {
    origin: 'A10',
    skipHeader: true,
  });

  const colWidths = [
    ...(title.includes('Mã SKU')
      ? [{ wch: 18 }, { wch: 48 }]
      : [{ wch: 48 }, { wch: 10 }]),

    ...total.map((item: any) => {
      return { wch: 10 };
    }),
    ...title.slice(total.length + 2).map((item: any) => {
      return { wch: 15 };
    }),
  ];

  // add cols width
  worksheet['!cols'] = colWidths;

  const workBookBlob = convertWorkBookToBlob(workbook);

  return addStyles(workBookBlob);
};

export const handleExportProductReportQuantity = ({
  list,
  dateTitle,
  dayTitle,
  quantityNameList,
  countDisplayColumnOneDate,
}): any => {
  const dateTitleOfColumn = dateTitle;
  const dayTitleOfColumn = dayTitle;
  const totalColumns = dateTitleOfColumn.length;
  const startGenTitleOfColumn = 1;

  const addStyles = (workbookBlob): string =>
    XlsxPopulate.fromDataAsync(workbookBlob).then(workbook => {
      workbook.sheets().forEach(sheet => {
        sheet.usedRange().style({
          fontFamily: 'Arial',
          border: 'thin',
          horizontalAlignment: 'left',
          verticalAlignment: 'center',
          wrapText: true,
        });
        sheet.column('A').width(20);
        sheet.row(1).height(60);
        sheet.row(2).height(20);

        getDummyArray(startGenTitleOfColumn - 1).forEach(element => {
          getDummyArray(totalColumns).forEach(item => {
            sheet
              .row(element.key + 1)
              .cell(item.key + 1)
              .style({
                border: 'none',
                wrapText: true,
              });
          });
        });

        getDummyArray(totalColumns).forEach(item => {
          if (item.key > 0) {
            sheet
              .row(startGenTitleOfColumn)
              .cell(item.key + 1)
              .style({
                fill: '93C47D',
                bold: 'true',
                verticalAlignment: 'center',
                horizontalAlignment: 'center',
              });
          }
          sheet
            .row(startGenTitleOfColumn + 1)
            .cell(item.key + 1)
            .style({
              bold: 'true',
              verticalAlignment: 'center',
              horizontalAlignment: 'center',
            });
        });
      });
      return workbook
        .outputAsync()
        .then(newWorkBookBlob => URL.createObjectURL(newWorkBookBlob));
    });

  // create new work book
  const workbook = XLSX.utils.book_new();
  // create empty worksheet
  const worksheet = XLSX.utils.json_to_sheet([]);

  // add worksheet into work book
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Trang tính 1');

  const merge = dateTitleOfColumn.reduce((result, item, index) => {
    if (index !== 0 && index % countDisplayColumnOneDate === 0) {
      return result.concat([
        {
          s: { c: index - countDisplayColumnOneDate + 1, r: 0 },
          e: { c: index, r: 0 },
        },
        {
          s: { c: index - countDisplayColumnOneDate + 1, r: 1 },
          e: { c: index, r: 1 },
        },
      ]);
    }

    return result;
  }, []);

  worksheet['!merges'] = merge;

  XLSX.utils.sheet_add_aoa(worksheet, [[...dateTitleOfColumn]], {
    origin: 'A1',
    cellStyles: true,
  });
  XLSX.utils.sheet_add_aoa(worksheet, [[...dayTitleOfColumn]], {
    origin: 'A2',
    cellStyles: true,
  });
  XLSX.utils.sheet_add_aoa(worksheet, [[...quantityNameList]], {
    origin: 'A3',
    cellStyles: true,
  });
  XLSX.utils.sheet_add_json(worksheet, list, {
    origin: 'A4',
    skipHeader: true,
  });

  const colWidths = [
    ...dateTitleOfColumn.map((item: any) => {
      return { wch: 8 };
    }),
  ];

  // add cols width
  worksheet['!cols'] = colWidths;

  const workBookBlob = convertWorkBookToBlob(workbook);

  return addStyles(workBookBlob);
};

export const handleExportOrders = ({ list, title, info }): any => {
  const TitleOfColumn = title;
  const leftColumns = ['A'];

  const totalColumns = TitleOfColumn.length;
  const startGenTitleOfColumn = 8;

  const addStyles = (workbookBlob): string =>
    XlsxPopulate.fromDataAsync(workbookBlob).then(workbook => {
      workbook.sheets().forEach(sheet => {
        sheet.usedRange().style({
          fontFamily: 'Arial',
          border: 'thin',
          horizontalAlignment: 'left',
          verticalAlignment: 'center',
          wrapText: true,
        });
        sheet.row(4).height(20);
        leftColumns.forEach(col => {
          sheet.column(col).style({
            horizontalAlignment: 'left',
            verticalAlignment: 'center',
          });
        });
        sheet.cell('A4').style({
          bold: 'true',
          border: 'none',
          fontSize: '13',
          horizontalAlignment: 'center',
          verticalAlignment: 'center',
        });

        sheet.cell('A1').style({
          bold: 'true',
          horizontalAlignment: 'left',
          verticalAlignment: 'center',
        });
        sheet.cell('A2').style({
          horizontalAlignment: 'left',
          verticalAlignment: 'center',
        });
        sheet.cell('A3').style({
          horizontalAlignment: 'center',
          verticalAlignment: 'center',
        });
        sheet.cell('A5').style({
          italic: 'true',
          horizontalAlignment: 'center',
          verticalAlignment: 'center',
        });
        sheet.cell('A6').style({
          italic: 'true',
          horizontalAlignment: 'center',
          verticalAlignment: 'center',
        });

        getDummyArray(startGenTitleOfColumn - 1).forEach(element => {
          getDummyArray(totalColumns).forEach(item => {
            sheet
              .row(element.key + 1)
              .cell(item.key + 1)
              .style({
                border: 'none',
                wrapText: true,
              });
          });
        });

        getDummyArray(totalColumns).forEach(item => {
          sheet
            .row(startGenTitleOfColumn)
            .cell(item.key + 1)
            .style({
              fill: '93C47D',
              bold: 'true',
              verticalAlignment: 'center',
            });
        });

        getDummyArray(5).forEach(item => {
          sheet.row(startGenTitleOfColumn + list.length + item.key + 2).style({
            border: 'none',
            bold: 'true',
            horizontalAlignment: 'left',
            verticalAlignment: 'center',
          });
        });
      });
      return workbook
        .outputAsync()
        .then(newWorkBookBlob => URL.createObjectURL(newWorkBookBlob));
    });

  // create new work book
  const workbook = XLSX.utils.book_new();
  // create empty worksheet
  const worksheet = XLSX.utils.json_to_sheet([]);

  // add worksheet into work book
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Trang tính 1');

  const merge = [
    { s: { c: 0, r: 3 }, e: { c: title.length, r: 3 } },
    { s: { c: 0, r: 4 }, e: { c: title.length, r: 4 } },

    ...title.map((item: any, index: number) => {
      return { s: { c: index, r: 7 }, e: { c: index, r: 8 } };
    }),
  ];

  worksheet['!merges'] = merge;

  XLSX.utils.sheet_add_aoa(worksheet, [[info.title]], {
    origin: 'A4',
    cellStyles: true,
  });

  XLSX.utils.sheet_add_aoa(worksheet, [[info.date]], {
    origin: 'A5',
    cellStyles: true,
  });

  XLSX.utils.sheet_add_aoa(worksheet, [[...TitleOfColumn]], {
    origin: 'A8',
    cellStyles: true,
  });

  XLSX.utils.sheet_add_json(worksheet, list, {
    origin: 'A10',
    skipHeader: true,
  });

  const colWidths = [
    ...title.map((item: any) => {
      return { wch: 25 };
    }),
  ];

  // add cols width
  worksheet['!cols'] = colWidths;

  const workBookBlob = convertWorkBookToBlob(workbook);

  return addStyles(workBookBlob);
};

export const handleExportExcel = ({
  list,
  info,
  columnOptions,
}: {
  list;
  info: string[];
  columnOptions: {
    columns: string[];
    centerColumns?: string[];
    rightColumns?: string[];
    leftColumns?: string[];
    isHasTotalRow?: boolean;
    mergeColumns?: string[];
    widthColumns?: Array<{
      title: string;
      size: number;
    }>;
  };
}): any => {
  const { columns, isHasTotalRow, leftColumns, rightColumns, mergeColumns } =
    columnOptions;

  const centerColumns = ['A'];

  const totalColumns = columns.length;
  const startGenTitleOfColumn = info.length + 1;

  const cloneList = JSON.parse(JSON.stringify(list));

  const addStyles = (workbookBlob): string =>
    XlsxPopulate.fromDataAsync(workbookBlob).then(workbook => {
      workbook.sheets().forEach(sheet => {
        sheet.usedRange().style({
          fontFamily: 'Arial',
          border: 'thin',
        });
        sheet.row(4).height(20);

        // Bold title
        const foundTitleIndex = info.findIndex(
          item => removeSpaceString(item).length != 0
        );
        sheet.cell(`A${foundTitleIndex + 1}`).style({
          bold: 'true',
          border: 'none',
          fontSize: '13',
          horizontalAlignment: 'center',
          verticalAlignment: 'center',
        });

        centerColumns.forEach(col => {
          sheet.column(col).style({
            horizontalAlignment: 'center',
            verticalAlignment: 'center',
          });
        });

        if (leftColumns?.length) {
          leftColumns.forEach(col => {
            sheet.column(col).style({
              horizontalAlignment: 'left',
              verticalAlignment: 'center',
            });
          });
        }

        if (rightColumns?.length) {
          rightColumns.forEach(col => {
            sheet.column(col).style({
              horizontalAlignment: 'right',
              verticalAlignment: 'center',
            });
          });
        }

        info.slice(foundTitleIndex + 1).forEach((item, index) => {
          sheet.cell(`A${foundTitleIndex + index + 2}`).style({
            italic: 'true',
          });
        });

        getDummyArray(startGenTitleOfColumn - 1).forEach(element => {
          getDummyArray(totalColumns).forEach(item => {
            sheet
              .row(element.key + 1)
              .cell(item.key + 1)
              .style({
                border: 'none',
              });
          });
        });

        // Fill color header
        getDummyArray(totalColumns).forEach(item => {
          sheet
            .row(startGenTitleOfColumn)
            .cell(item.key + 1)
            .style({
              fill: '93C47D',
              bold: 'true',
              verticalAlignment: 'center',
            });
        });

        if (isHasTotalRow) {
          getDummyArray(totalColumns).forEach(item => {
            sheet
              .row(startGenTitleOfColumn + cloneList.length + 1)
              .cell(item.key + 1)
              .style({
                fill: '93C47D',
                bold: 'true',
                verticalAlignment: 'center',
              });
          });
        }

        if (mergeColumns?.length) {
          for (let i = 0; i < cloneList.length; i += 1) {
            if (cloneList[i].isMerge) {
              getDummyArray(totalColumns).forEach(item => {
                sheet
                  .row(startGenTitleOfColumn + i + 2)
                  .cell(item.key + 1)
                  .style({
                    fill: 'ECF4E7',
                    bold: 'true',
                    verticalAlignment: 'center',
                  });
              });
            }
          }
        }
      });
      return workbook
        .outputAsync()
        .then(newWorkBookBlob => URL.createObjectURL(newWorkBookBlob));
    });

  // create new work book
  const workbook = XLSX.utils.book_new();
  // create empty worksheet
  const worksheet = XLSX.utils.json_to_sheet([]);

  // add worksheet into work book
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Trang tính 1');

  const merge: any[] = [];

  // merge header info
  info.forEach((item, index) => {
    merge.push({
      s: { c: 0, r: index },
      e: { c: columns.length - 1, r: index },
    });
  });

  // merge header column
  columns.forEach((item, index) => {
    merge.push({
      s: { c: index, r: info.length },
      e: { c: index, r: info.length + 1 },
    });
  });

  if (mergeColumns?.length) {
    for (let i = 0; i < list.length; i += 1) {
      if (cloneList[i].isMerge) {
        merge.push({
          s: { c: 1, r: startGenTitleOfColumn + i + 1 },
          e: { c: columns.length - 1, r: startGenTitleOfColumn + i + 1 },
        });
      }
    }
  }

  worksheet['!merges'] = merge;

  info.forEach((item, index) => {
    XLSX.utils.sheet_add_aoa(worksheet, [[item]], {
      origin: `A${index + 1}`,
      cellStyles: true,
    });
  });

  XLSX.utils.sheet_add_aoa(worksheet, [[...columns]], {
    origin: `A${info.length + 1}`,
    cellStyles: true,
  });

  if (mergeColumns?.length) {
    const formatList = list.map(item => {
      delete item.isMerge;
      return item;
    });
    XLSX.utils.sheet_add_json(worksheet, formatList, {
      origin: `A${info.length + 3}`,
      skipHeader: true,
    });
  } else {
    XLSX.utils.sheet_add_json(worksheet, list, {
      origin: `A${info.length + 3}`,
      skipHeader: true,
    });
  }

  let colWidths = [
    { wch: 15 },
    { wch: 25 },
    { wch: 20 },
    { wch: 15 },
    { wch: 15 },
    { wch: 15 },
    { wch: 15 },
  ];

  if (columnOptions?.widthColumns?.length) {
    const customColWidths: any[] = [];
    columnOptions?.columns?.forEach(item => {
      const foundColumn = columnOptions?.widthColumns?.find(
        sizeItem => sizeItem.title == item
      );

      if (foundColumn) {
        customColWidths.push({
          wch: foundColumn.size,
        });
      } else {
        customColWidths.push({
          wch: 15,
        });
      }
    });

    colWidths = customColWidths;
  }

  // add cols width
  worksheet['!cols'] = colWidths;

  const workBookBlob = convertWorkBookToBlob(workbook);

  return addStyles(workBookBlob);
};

export const handleExportProof = ({ list, info }): any => {
  const TitleOfColumn = [
    'Ngày',
    'Số chứng từ',
    'Loại chứng từ',
    'Diễn giải',
    'Tăng trong kỳ',
    'Giảm trong kỳ',
    'Số dư công nợ',
  ];

  const centerColumns = ['A'];
  const leftColumns = ['B', 'C', 'D'];
  const rightColumns = ['E', 'F', 'G'];
  const moneyFormatColumns = ['E', 'F', 'G'];

  const totalColumns = TitleOfColumn.length;
  const startGenTitleOfColumn = 8;

  const addStyles = (workbookBlob): string =>
    XlsxPopulate.fromDataAsync(workbookBlob).then(workbook => {
      workbook.sheets().forEach(sheet => {
        sheet.usedRange().style({
          fontFamily: 'Times New Roman',
          border: 'thin',
        });
        sheet.row(4).height(20);
        sheet.cell('A4').style({
          bold: 'true',
          border: 'none',
          fontSize: '13',
          horizontalAlignment: 'center',
          verticalAlignment: 'center',
        });

        centerColumns.forEach(col => {
          sheet.column(col).style({
            horizontalAlignment: 'center',
            verticalAlignment: 'center',
          });
        });
        leftColumns.forEach(col => {
          sheet.column(col).style({
            horizontalAlignment: 'left',
            verticalAlignment: 'center',
          });
        });
        rightColumns.forEach(col => {
          sheet.column(col).style({
            horizontalAlignment: 'right',
            verticalAlignment: 'center',
          });
        });
        rightColumns
          .filter(col => moneyFormatColumns.includes(col))
          .forEach(col => {
            sheet.column(col).style({
              horizontalAlignment: 'right',
              verticalAlignment: 'center',
              numberFormat: '#,##0',
            });
          });

        sheet.cell('A1').style({
          horizontalAlignment: 'left',
          verticalAlignment: 'center',
          bold: 'true',
        });
        sheet.cell('A2').style({
          horizontalAlignment: 'left',
          verticalAlignment: 'center',
        });
        sheet.cell('A3').style({
          horizontalAlignment: 'left',
          verticalAlignment: 'center',
        });
        sheet.cell('A5').style({
          italic: 'true',
        });
        sheet.cell('A6').style({
          italic: 'true',
        });

        getDummyArray(startGenTitleOfColumn - 1).forEach(element => {
          getDummyArray(totalColumns).forEach(item => {
            sheet
              .row(element.key + 1)
              .cell(item.key + 1)
              .style({
                border: 'none',
              });
          });
        });

        getDummyArray(totalColumns).forEach(item => {
          sheet
            .row(startGenTitleOfColumn)
            .cell(item.key + 1)
            .style({
              fill: '93C47D',
              bold: 'true',
              verticalAlignment: 'center',
            });
        });
        getDummyArray(totalColumns).forEach(item => {
          sheet
            .row(startGenTitleOfColumn + list.length + 1)
            .cell(item.key + 1)
            .style({
              fill: '93C47D',
              bold: 'true',
              verticalAlignment: 'center',
            });
        });

        for (let i = 0; i < list.length; i += 1) {
          if (!list[i].createdAt && !list[i].entityDocumentSubCategoryName) {
            getDummyArray(totalColumns).forEach(item => {
              sheet
                .row(8 + i + 2)
                .cell(item.key + 1)
                .style({
                  bold: 'true',
                  verticalAlignment: 'center',
                });
            });
          }
        }
      });
      return workbook
        .outputAsync()
        .then(newWorkBookBlob => URL.createObjectURL(newWorkBookBlob));
    });

  // create new work book
  const workbook = XLSX.utils.book_new();
  // create empty worksheet
  const worksheet = XLSX.utils.json_to_sheet([]);

  // add worksheet into work book
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Trang tính 1');

  const merge = [
    { s: { c: 0, r: 3 }, e: { c: 6, r: 3 } },
    { s: { c: 0, r: 4 }, e: { c: 6, r: 4 } },
    { s: { c: 0, r: 5 }, e: { c: 6, r: 5 } },

    { s: { c: 0, r: 7 }, e: { c: 0, r: 8 } },
    { s: { c: 1, r: 7 }, e: { c: 1, r: 8 } },
    { s: { c: 2, r: 7 }, e: { c: 2, r: 8 } },
    { s: { c: 3, r: 7 }, e: { c: 3, r: 8 } },
    { s: { c: 4, r: 7 }, e: { c: 4, r: 8 } },
    { s: { c: 5, r: 7 }, e: { c: 5, r: 8 } },
    { s: { c: 6, r: 7 }, e: { c: 6, r: 8 } },
  ];

  worksheet['!merges'] = merge;

  XLSX.utils.sheet_add_aoa(worksheet, [[info.supplierName]], {
    origin: 'A1',
    cellStyles: true,
  });
  XLSX.utils.sheet_add_aoa(worksheet, [[info.supplierAddress]], {
    origin: 'A2',
    cellStyles: true,
  });
  XLSX.utils.sheet_add_aoa(worksheet, [[info.supplierPhoneNumber]], {
    origin: 'A3',
    cellStyles: true,
  });

  XLSX.utils.sheet_add_aoa(worksheet, [[info.title]], {
    origin: 'A4',
    cellStyles: true,
  });

  XLSX.utils.sheet_add_aoa(worksheet, [[info.time]], {
    origin: 'A5',
    cellStyles: true,
  });

  XLSX.utils.sheet_add_aoa(worksheet, [[info.customerInfo]], {
    origin: 'A6',
    cellStyles: true,
  });

  XLSX.utils.sheet_add_aoa(worksheet, [[...TitleOfColumn]], {
    origin: 'A8',
    cellStyles: true,
  });

  XLSX.utils.sheet_add_json(worksheet, list, {
    origin: 'A10',
    skipHeader: true,
  });

  const colWidths = [
    { wch: 15 },
    { wch: 25 },
    { wch: 20 },
    { wch: 15 },
    { wch: 15 },
    { wch: 15 },
    { wch: 15 },
  ];

  // add cols width
  worksheet['!cols'] = colWidths;

  const workBookBlob = convertWorkBookToBlob(workbook);

  return addStyles(workBookBlob);
};
